/**
 * Shared HTTP client for the Worker admin surfaces.
 *
 * Every admin sub-app (LLM / signups / users / audit) is cookie-credentialed
 * and 404-gated for non-admins (so the route's existence is never leaked).
 * The fetch wrapper + the `AdminResult` discriminated union were previously
 * copy-pasted into `api.ts`, `signups-api.ts`, and `users-api.ts`; they now
 * live here. Each api module binds the helper to its base path:
 *
 *   const request = createAdminRequest('/v1/admin/users');
 */
import { workerOrigin } from '../worker-origin.js';

export type AdminResult<T> =
  | { ok: true; value: T }
  | { ok: false; kind: 'not-found' }
  | { ok: false; kind: 'unauthenticated' }
  | { ok: false; kind: 'error'; status: number; error: string };

interface ErrorBody {
  error?: string;
}

export type AdminRequest = <T>(path: string, init?: RequestInit) => Promise<AdminResult<T>>;

/**
 * Build a `request<T>(path, init)` bound to an admin sub-app base path.
 * Maps 401 → `unauthenticated`, 404 → `not-found`, other non-2xx → `error`,
 * and network failures → `error` with status 0.
 */
export function createAdminRequest(basePath: string): AdminRequest {
  return async function request<T>(
    path: string,
    init?: RequestInit,
  ): Promise<AdminResult<T>> {
    try {
      const res = await fetch(`${workerOrigin()}${basePath}${path}`, {
        credentials: 'include',
        headers: {
          accept: 'application/json',
          ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(init?.headers ?? {}),
        },
        ...init,
      });
      if (res.status === 401) return { ok: false, kind: 'unauthenticated' };
      if (res.status === 404) return { ok: false, kind: 'not-found' };
      if (!res.ok) {
        let parsed: ErrorBody = {};
        try {
          parsed = (await res.json()) as ErrorBody;
        } catch {
          // non-JSON body — fall through to the status-based fallback
        }
        return {
          ok: false,
          kind: 'error',
          status: res.status,
          error: parsed.error ?? `Request failed (${res.status}).`,
        };
      }
      const value = (await res.json()) as T;
      return { ok: true, value };
    } catch (err) {
      return {
        ok: false,
        kind: 'error',
        status: 0,
        error: err instanceof Error ? err.message : 'Network error.',
      };
    }
  };
}
